import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  addAuthorTypes,
  addDeclarationsTypes,
  addFileTypes,
  addSuggestedReviewerTypes,
  applyAuthorChangesTypes,
  articleAuthorTypes,
  authorHistoryTypes,
  Authors,
  authortype,
  deleteFileTypes,
  updateFileVisibilityTypes,
  deleteSuggestedReviewerTypes,
  editAuthorTypes,
  fileTypes,
  putAuthorContibutionsTypes,
  removeAuthorTypes,
  setAuthorsOrderTypes,
  setMainAuthortypes,
  SuccessType,
  veredictsHistoryTypes,
  updateReviewFileVisibilityTypes,
  editSuggestedReviewerTypes,
} from "./authorTypes";
import authorService from "./authorService";
import {
  ArticleType,
  editArticleTypes,
  sendArticleType,
} from "../articles/articlesTypes";
import { suggestedReviewerTypes } from "../review/reviewTypes";
import articlesService from "../articles/articlesService";

interface AuthorState {
  authors: Authors | null;
  loggedAuthor: authortype | null;
  author: authortype | null;
  suggestedReviewers: suggestedReviewerTypes[];
  articleAuthors: articleAuthorTypes[] | null;
  article_slug: string | null;
  article: ArticleType | null;
  veredictsHistory: veredictsHistoryTypes[] | null;
  authorHistory: authorHistoryTypes[] | null;
  myArticles: ArticleType[] | null;
  loading: boolean;
  error: any | null;
  errorStatus: number | null;
  articleAuthorError: string | null;
  files: fileTypes[];
}

const initialState: AuthorState = {
  authors: null,
  author: null,
  loggedAuthor: null,
  suggestedReviewers: [],
  articleAuthors: null,
  article_slug: null,
  article: null,
  veredictsHistory: null,
  authorHistory: null,
  myArticles: null,
  loading: false,
  error: "",
  errorStatus: null,
  articleAuthorError: null,
  files: [],
};

export const sendArticle = createAsyncThunk<
  { article: ArticleType; article_slug: string; authors: articleAuthorTypes[] },
  sendArticleType,
  { rejectValue: string }
>("author/sendArticle", async (article: sendArticleType, thunkAPI) => {
  try {
    const createdArticle = await authorService.sendArticle(article);

    if (!createdArticle) {
      throw new Error("El servidor no devolvió un artículo válido.");
    }

    return createdArticle;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.message || "Error desconocido");
  }
});

export const getAuthors = createAsyncThunk<
  Authors,
  void,
  { rejectValue: string }
>("author/getAuthors", async (_, thunkAPI) => {
  try {
    const res = await authorService.getAuthors();
    return res;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.message || "Error desconocido");
  }
});

export const getAuthorBySlug = createAsyncThunk<
  authortype,
  { authorSlug: string; articleSlug: string },
  { rejectValue: string }
>("author/getAuthorBySlug", async ({ authorSlug, articleSlug }, thunkAPI) => {
  try {
    const res = await authorService.getAuthorBySlug({
      authorSlug,
      articleSlug,
    });
    return res;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.message || "Error desconocido");
  }
});

export const getMyArticles = createAsyncThunk<
  ArticleType[],
  void,
  { rejectValue: string }
>("author/getMyArticles", async (_, { rejectWithValue }) => {
  try {
    const res = await authorService.getMyArticles();
    return res;
  } catch (error: any) {
    return rejectWithValue(error.message || "Error desconocido");
  }
});

export const getAuthorMe = createAsyncThunk<
  authortype,
  void,
  { rejectValue: string }
>("author/getAuthorMe", async (_, { rejectWithValue }) => {
  try {
    const res = await authorService.getAuthorMe();
    return res;
  } catch (error: any) {
    return rejectWithValue(error.message || "Error desconocido");
  }
});

export const createAuthor = createAsyncThunk<
  string,
  any,
  { rejectValue: string }
>("author/createAuthor", async (data: any, { rejectWithValue }) => {
  try {
    const response = await authorService.createAuthor(data);
    return response;
  } catch (error: any) {
    return rejectWithValue(error.message || "Error desconocido");
  }
});

export const addAuthor = createAsyncThunk<
  articleAuthorTypes[],
  addAuthorTypes,
  { rejectValue: string }
>("author/addAuthor", async (data: addAuthorTypes, { rejectWithValue }) => {
  try {
    const response = await authorService.addAuthor(data);

    return response;
  } catch (error: any) {
    return rejectWithValue(error.message || "Error desconocido");
  }
});

export const setMainAuthor = createAsyncThunk<
  articleAuthorTypes[],
  setMainAuthortypes,
  { rejectValue: string }
>(
  "author/setMainAuthor",
  async (data: setMainAuthortypes, { rejectWithValue }) => {
    try {
      const response = await authorService.setMainAuthor(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || "Error desconocido");
    }
  },
);

export const setMailingAuthor = createAsyncThunk<
  articleAuthorTypes[],
  setMainAuthortypes,
  { rejectValue: string }
>(
  "author/setMailingAuthor",
  async (data: setMainAuthortypes, { rejectWithValue }) => {
    try {
      const response = await authorService.setMailingAuthor(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || "Error desconocido");
    }
  },
);

export const addFile = createAsyncThunk<
  fileTypes[],
  addFileTypes,
  { rejectValue: any }
>("author/addFile", async (data: addFileTypes, { rejectWithValue }) => {
  try {
    const response = await authorService.addFile(data);
    return response;
  } catch (error: any) {
    return rejectWithValue(error);
  }
});

export const addOthersFiles = createAsyncThunk<
  fileTypes[],
  addFileTypes,
  { rejectValue: any }
>("author/addOthersFiles", async (data: addFileTypes, { rejectWithValue }) => {
  try {
    const response = await authorService.addOthersFiles(data);
    return response;
  } catch (error: any) {
    return rejectWithValue(error);
  }
});

export const getFiles = createAsyncThunk<
  fileTypes[],
  string,
  { rejectValue: string }
>("author/getFiles", async (slug: string, { rejectWithValue }) => {
  try {
    const files: fileTypes[] = await authorService.getFiles(slug);
    return files;
  } catch (error: any) {
    return rejectWithValue(error.message || "Error desconocido");
  }
});
export const getMainFile = createAsyncThunk<
  string,
  number,
  { rejectValue: string }
>("author/getMainFile", async (id: number, { rejectWithValue }) => {
  try {
    const file: string = await authorService.getMainFile(id);
    return file;
  } catch (error: any) {
    return rejectWithValue(error.message || "Error desconocido");
  }
});
export const addDeclarations = createAsyncThunk<
  SuccessType,
  addDeclarationsTypes,
  { rejectValue: string }
>(
  "author/addDeclarations",
  async (data: addDeclarationsTypes, { rejectWithValue }) => {
    try {
      const response = await authorService.addDeclarations(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || "Error desconocido");
    }
  },
);
export const addSuggestedReviewer = createAsyncThunk<
  suggestedReviewerTypes[],
  addSuggestedReviewerTypes,
  { rejectValue: string }
>(
  "author/addSuggestedReviewer",
  async (data: addSuggestedReviewerTypes, { rejectWithValue }) => {
    try {
      const response = await authorService.addSuggestedReviewer(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || "Error desconocido");
    }
  },
);

export const editSuggestedReviewer = createAsyncThunk<
  suggestedReviewerTypes[],
  editSuggestedReviewerTypes,
  { rejectValue: string }
>(
  "author/editSuggestedReviewer",
  async (data: editSuggestedReviewerTypes, { rejectWithValue }) => {
    try {
      const response = await authorService.editSuggestedReviewer(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || "Error desconocido");
    }
  },
);

export const confirmArticle = createAsyncThunk<
  SuccessType,
  string,
  { rejectValue: string }
>("author/confirmArticle", async (slug: string, { rejectWithValue }) => {
  try {
    const response = await authorService.confirmArticle(slug);
    return response;
  } catch (error: any) {
    return rejectWithValue(error.message || "Error desconocido");
  }
});

export const applyChanges = createAsyncThunk<
  SuccessType,
  string,
  { rejectValue: string }
>("author/applyChanges", async (slug: string, { rejectWithValue }) => {
  try {
    const response = await authorService.applyChanges(slug);
    return response;
  } catch (error: any) {
    return rejectWithValue(error.message || "Error desconocido");
  }
});

export const getSuggestedReviewers = createAsyncThunk<
  suggestedReviewerTypes[],
  string,
  { rejectValue: string }
>(
  "author/getSuggestedReviewers",
  async (article_slug: string, { rejectWithValue }) => {
    try {
      const reviewers = await authorService.getSuggestedReviewers(article_slug);
      return reviewers;
    } catch (error: any) {
      return rejectWithValue(error.message || "Error desconocido");
    }
  },
);

export const deleteAuthor = createAsyncThunk<
  SuccessType,
  string,
  { rejectValue: string }
>("author/deleteAuthor", async (author_slug: string, { rejectWithValue }) => {
  try {
    const response = await authorService.deleteAuthor(author_slug);
    return response;
  } catch (error: any) {
    return rejectWithValue(error.message || "Error desconocido");
  }
});

export const deleteFile = createAsyncThunk<
  fileTypes[],
  deleteFileTypes,
  { rejectValue: string }
>("author/deleteFile", async (data: deleteFileTypes, { rejectWithValue }) => {
  try {
    const response = await authorService.deleteFile(data);
    return response;
  } catch (error: any) {
    return rejectWithValue(error.message || "Error desconocido");
  }
});

export const updateFileVisibility = createAsyncThunk<
  SuccessType,
  updateFileVisibilityTypes,
  { rejectValue: string }
>(
  "author/updateFileVisibility",
  async (data: updateFileVisibilityTypes, { rejectWithValue }) => {
    try {
      const response = await authorService.updateFileVisibility(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || "Error desconocido");
    }
  },
);

export const updateReviewFileVisibility = createAsyncThunk<
  SuccessType,
  updateReviewFileVisibilityTypes,
  { rejectValue: string }
>(
  "author/updateReviewFileVisibility",
  async (data: updateReviewFileVisibilityTypes, { rejectWithValue }) => {
    try {
      const response = await authorService.updateReviewFileVisibility(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || "Error desconocido");
    }
  },
);

export const deleteSuggestedReviewer = createAsyncThunk<
  suggestedReviewerTypes[],
  deleteSuggestedReviewerTypes,
  { rejectValue: string }
>(
  "author/deleteSuggestedReviewer",
  async (data: deleteSuggestedReviewerTypes, { rejectWithValue }) => {
    try {
      const response = await authorService.deleteSuggestedReviewer(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || "Error desconocido");
    }
  },
);

export const deleteArticle = createAsyncThunk<
  SuccessType,
  string,
  { rejectValue: string }
>("author/deleteArticle", async (article_slug: string, { rejectWithValue }) => {
  try {
    const response = await authorService.deleteArticle(article_slug);
    return response;
  } catch (error: any) {
    return rejectWithValue(error.message || "Error desconocido");
  }
});

export const applyAuthorChanges = createAsyncThunk<
  SuccessType,
  applyAuthorChangesTypes,
  { rejectValue: string }
>(
  "author/applyAuthorChanges",
  async (data: applyAuthorChangesTypes, { rejectWithValue }) => {
    try {
      const response = await authorService.applyAuthorChanges(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || "Error desconocido");
    }
  },
);

export const getVeredictsHistory = createAsyncThunk<
  veredictsHistoryTypes[],
  string,
  { rejectValue: string }
>("editor/getArticlesHistory", async (slug: string, { rejectWithValue }) => {
  try {
    const response = await authorService.getVeredictsHistory(slug);
    return response;
  } catch (error: any) {
    return rejectWithValue(error.message || "Error desconocido");
  }
});

export const editAuthor = createAsyncThunk<
  authortype,
  editAuthorTypes,
  { rejectValue: string }
>("author/editAuthor", async (data: editAuthorTypes, { rejectWithValue }) => {
  try {
    const response = await authorService.editAuthor(data);
    return response;
  } catch (error: any) {
    return rejectWithValue(error);
  }
});
export const editAuthorInArticle = createAsyncThunk<
  authortype,
  editAuthorTypes,
  { rejectValue: string }
>(
  "author/editAuthorInArticle",
  async (data: editAuthorTypes, { rejectWithValue }) => {
    try {
      const response = await authorService.editAuthorInArticle(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);
export const removeAuthor = createAsyncThunk<
  articleAuthorTypes[],
  removeAuthorTypes,
  { rejectValue: string }
>(
  "author/removeAuthor",
  async (data: removeAuthorTypes, { rejectWithValue }) => {
    try {
      const response = await authorService.removeAuthor(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const setAuthorsOrder = createAsyncThunk<
  SuccessType,
  setAuthorsOrderTypes,
  { rejectValue: string }
>(
  "author/setAuthorsOrder",
  async (data: setAuthorsOrderTypes, { rejectWithValue }) => {
    try {
      const response = await authorService.setAuthorsOrder(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const putAuthorContributions = createAsyncThunk<
  SuccessType,
  putAuthorContibutionsTypes,
  { rejectValue: string }
>(
  "author/putAuthorContributions",
  async (data: putAuthorContibutionsTypes, { rejectWithValue }) => {
    try {
      const response = await authorService.putAuthorContributions(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const getAuthorHistory = createAsyncThunk<
  authorHistoryTypes[],
  string,
  { rejectValue: string }
>(
  "author/getAuthorHistory",
  async (article_slug: string, { rejectWithValue }) => {
    try {
      const res = await authorService.getAuthorHistory(article_slug);
      return Array.isArray(res) ? res : [res];
    } catch (error: any) {
      return rejectWithValue(error.message || "Error desconocido");
    }
  },
);

export const setContribution = createAsyncThunk<
  any,
  any,
  { rejectValue: string }
>(
  "author/setContribution",
  async (
    data: {
      article_slug: string;
      author_slug: string;
      contribution: number;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await authorService.setContribution(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const editArticle = createAsyncThunk<
  any,
  editArticleTypes,
  { rejectValue: string }
>(
  "articles/editArticle",
  async (articleEditRequest: editArticleTypes, { rejectWithValue }) => {
    try {
      const response = await articlesService.editArticles(articleEditRequest);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  },
);

export const getArticleAuthors = createAsyncThunk<
  articleAuthorTypes[],
  string,
  { rejectValue: string }
>("author/getArticleAuthors", async (slug: string, { rejectWithValue }) => {
  try {
    return await authorService.getArticleAuthors(slug);
  } catch (error: any) {
    return rejectWithValue(error);
  }
});

export const getArticleBySlugAuthor = createAsyncThunk<
  ArticleType,
  string,
  { rejectValue: string }
>(
  "author/getArticleBySlugAuthor",
  async (slug: string, { rejectWithValue }) => {
    try {
      const article = await articlesService.getArticleBySlug(slug);
      const cleanField = (value: any) =>
        typeof value === "string" ? value.replace(/\\/g, "") : value;
      const cleanedArticle = Object.fromEntries(
        Object.entries(article as Record<string, any>).map(([k, v]) => [
          k,
          cleanField(v),
        ]),
      ) as ArticleType;
      return cleanedArticle;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);
const authorSlice = createSlice({
  name: "author",
  initialState,
  reducers: {
    resetSubmit: (state) => {
      state.article = null;
      state.article_slug = null;
      state.articleAuthors = null;
      state.error = null;
    },
    resetArticleAuthors: (state) => {
      state.articleAuthors = null;
      state.error = null;
    },
    createAuthorReset: (state) => {
      state.error = null;
    },
    resetFiles: (state) => {
      state.files = [];
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // sendArticle
    builder
      .addCase(sendArticle.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendArticle.fulfilled, (state, action: any) => {
        state.loading = false;
        state.article = action.payload.article;
        state.article_slug = action.payload.article_slug;
        state.articleAuthors = action.payload.author;
      })
      .addCase(sendArticle.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error al enviar el artículo";
      });
    // editArticle
    builder
      .addCase(editArticle.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editArticle.fulfilled, (state, action: any) => {
        state.loading = false;
        state.article = action.payload.article;
        state.article_slug = action.payload.article_slug;
        state.articleAuthors = action.payload.author;
      })
      .addCase(editArticle.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error al enviar el artículo";
      });

    // getAuthors
    builder
      .addCase(getAuthors.pending, (state) => {
        state.loading = true;
        state.authors = null;
        state.error = null;
      })
      .addCase(
        getAuthors.fulfilled,
        (state, action: PayloadAction<Authors>) => {
          state.loading = false;
          state.authors = action.payload;
        },
      )
      .addCase(getAuthors.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error al obtener autores";
      });

    // getMyArticles
    builder
      .addCase(getMyArticles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getMyArticles.fulfilled,
        (state, action: PayloadAction<ArticleType[]>) => {
          state.loading = false;
          state.myArticles = action.payload;
        },
      )
      .addCase(getMyArticles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error al cargar mis artículos";
      });

    // getarticleBySlugAuthor
    builder
      .addCase(getArticleBySlugAuthor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getArticleBySlugAuthor.fulfilled,
        (state, action: PayloadAction<ArticleType>) => {
          state.loading = false;
          state.article = action.payload;
        },
      )
      .addCase(getArticleBySlugAuthor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error al cargar el artículo";
      });

    // getAuthorMe
    builder
      .addCase(getAuthorMe.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getAuthorMe.fulfilled,
        (state, action: PayloadAction<authortype>) => {
          state.loading = false;
          state.loggedAuthor = action.payload;
        },
      )
      .addCase(getAuthorMe.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error al cargar perfil del autor";
      });

    // createAuthor
    builder
      .addCase(createAuthor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAuthor.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createAuthor.rejected, (state) => {
        state.loading = false;
      });

    // addAuthor
    builder
      .addCase(addAuthor.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.articleAuthorError = null;
      })
      .addCase(addAuthor.fulfilled, (state, action) => {
        state.loading = false;
        state.articleAuthors = action.payload;
      })
      .addCase(addAuthor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error al agregar autor al artículo";
      });

    // setMainAuthor
    builder
      .addCase(setMainAuthor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(setMainAuthor.fulfilled, (state, action) => {
        state.loading = false;
        state.articleAuthors = action.payload;
        state.error = null;
      })
      .addCase(setMainAuthor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error al establecer autor principal";
      });

    // setMailingAuthor
    builder
      .addCase(setMailingAuthor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(setMailingAuthor.fulfilled, (state, action) => {
        state.loading = false;
        state.articleAuthors = action.payload;
        state.error = null;
      })
      .addCase(setMailingAuthor.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload || "Error al establecer autor de correspondencia";
      });

    // addFile
    builder
      .addCase(addFile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addFile.fulfilled, (state, action) => {
        state.loading = false;
        state.files = action.payload;
      })
      .addCase(addFile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload; // Guardas el objeto error completo
        state.errorStatus =
          action.payload?.status || action.payload?.response?.status;
      });

    // addOthersFiles
    builder
      .addCase(addOthersFiles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addOthersFiles.fulfilled, (state, action) => {
        state.loading = false;
        state.files = action.payload;
      })
      .addCase(addOthersFiles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error al subir archivos adicionales";
      });

    // getFiles
    builder
      .addCase(getFiles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getFiles.fulfilled,
        (state, action: PayloadAction<fileTypes[]>) => {
          state.loading = false;
          state.files = action.payload;
        },
      )
      .addCase(getFiles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error al cargar archivos";
      });

    // addDeclarations
    builder
      .addCase(addDeclarations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addDeclarations.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(addDeclarations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error al agregar declaraciones";
      });

    // addSuggestedReviewer
    builder
      .addCase(addSuggestedReviewer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addSuggestedReviewer.fulfilled, (state, action) => {
        state.loading = false;
        state.suggestedReviewers = action.payload;
      })
      .addCase(addSuggestedReviewer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error al sugerir revisor";
      });

    builder
      .addCase(editSuggestedReviewer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editSuggestedReviewer.fulfilled, (state, action) => {
        state.loading = false;
        state.suggestedReviewers = action.payload;
      })
      .addCase(editSuggestedReviewer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error al editar revisor sugerido";
      });

    // confirmArticle
    builder
      .addCase(confirmArticle.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(confirmArticle.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(confirmArticle.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error al confirmar artículo";
      });

    // applyChanges
    builder
      .addCase(applyChanges.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(applyChanges.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(applyChanges.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error al aplicar cambios";
      });

    // getSuggestedReviewers
    builder
      .addCase(getSuggestedReviewers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getSuggestedReviewers.fulfilled,
        (state, action: PayloadAction<suggestedReviewerTypes[]>) => {
          state.loading = false;
          state.suggestedReviewers = action.payload;
        },
      )
      .addCase(getSuggestedReviewers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error al cargar revisores sugeridos";
      });

    // deleteAuthor
    builder
      .addCase(deleteAuthor.pending, (state) => {
        state.loading = true;
        state.authors = null;
        state.error = null;
      })
      .addCase(deleteAuthor.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteAuthor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error al eliminar autor";
      });

    // deleteFile
    builder
      .addCase(deleteFile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteFile.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.files = action.payload;
      })
      .addCase(deleteFile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error al eliminar archivo";
      });

    // deleteSuggestedReviewer
    builder
      .addCase(deleteSuggestedReviewer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteSuggestedReviewer.fulfilled, (state, action) => {
        state.loading = false;
        state.suggestedReviewers = action.payload;
      })
      .addCase(deleteSuggestedReviewer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error al eliminar revisor sugerido";
      });

    // deleteArticle
    builder
      .addCase(deleteArticle.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteArticle.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteArticle.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error al eliminar artículo";
      });

    // applyAuthorChanges
    builder
      .addCase(applyAuthorChanges.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(applyAuthorChanges.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(applyAuthorChanges.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error al aplicar cambios en autor";
      });

    // getVeredictsHistory
    builder
      .addCase(getVeredictsHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getVeredictsHistory.fulfilled,
        (state, action: PayloadAction<veredictsHistoryTypes[]>) => {
          state.loading = false;
          state.veredictsHistory = action.payload;
        },
      )
      .addCase(getVeredictsHistory.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload || "Error al cargar historial de veredictos";
      });

    // getAuthorBySlug
    builder
      .addCase(getAuthorBySlug.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAuthorBySlug.fulfilled, (state, action) => {
        state.loading = false;
        state.author = action.payload;
      })
      .addCase(getAuthorBySlug.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error al editar autor";
      });

    // editAuthor
    builder
      .addCase(editAuthor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editAuthor.fulfilled, (state, action) => {
        state.loading = false;
        state.author = action.payload;
      })
      .addCase(editAuthor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error al editar autor";
      });
    // editAuthorInArticle
    builder
      .addCase(editAuthorInArticle.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editAuthorInArticle.fulfilled, (state, action) => {
        state.loading = false;
        state.author = action.payload;
      })
      .addCase(editAuthorInArticle.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error al editar autor";
      });

    // removeAuthor
    builder
      .addCase(removeAuthor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeAuthor.fulfilled, (state, action) => {
        state.loading = false;
        state.articleAuthors = action.payload;
      })
      .addCase(removeAuthor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error al remover autor del artículo";
      });

    // setAuthorsOrder
    builder
      .addCase(setAuthorsOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(setAuthorsOrder.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(setAuthorsOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error al ordenar autores";
      });

    // putAuthorContributions
    builder
      .addCase(putAuthorContributions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(putAuthorContributions.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(putAuthorContributions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error al actualizar contribuciones";
      });
    // setContributions
    builder
      .addCase(setContribution.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(setContribution.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.articleAuthors = action.payload.authors;
        state.author = action.payload.author;
      })
      .addCase(setContribution.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error al actualizar contribuciones";
      });

    // getAuthorHistory
    builder
      .addCase(getAuthorHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getAuthorHistory.fulfilled,
        (state, action: PayloadAction<authorHistoryTypes[]>) => {
          state.loading = false;
          state.authorHistory = action.payload;
        },
      )
      .addCase(getAuthorHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error al cargar historial de autores";
      });

    // getArticleAuthors
    builder
      .addCase(getArticleAuthors.pending, (state) => {
        state.loading = true;
        state.articleAuthors = null;
        state.error = null;
      })
      .addCase(getArticleAuthors.fulfilled, (state, action) => {
        state.loading = false;
        state.articleAuthors = action.payload;
        state.error = null;
      })
      .addCase(getArticleAuthors.rejected, (state, action) => {
        state.loading = false;
        state.articleAuthors = null;
        state.error = action.payload || "Error al obtener autores";
      });
    builder
      .addCase(getMainFile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMainFile.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(getMainFile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error al obtener archivo principal";
      });
  },
});

export const {
  resetSubmit,
  createAuthorReset,
  resetArticleAuthors,
  resetFiles,
} = authorSlice.actions;
export default authorSlice.reducer;
